// websocket/combat-sweep-truth.ts — R-131 slices J2 + J3 (Janus lanes N5d / N5e, 2026-10-02): the PERCEPTION and NOISE sweeps of an open fight,
// decided by a GM page.
//
// Replaces every caller of the server's ft_combat_enrol_sweep() (combat_membership.php — deleted with J3):
//   (a) the privacy REVEAL — a creature of the held encounter turned public while a fight is open (update_privacy_inc.php ran the sweep; that call
//       is deleted) — seen through the `encounter_creature_private_toggle` frame every page receives;
//   (b) the ROUND WRAP — the held fight's round went up (push_time_to_next_turn_inc.php ran the sweep on the legacy End Turn; deleted by the turn lane);
//   (c) the hostile action's NOISE (J3, lane N5e) — janus()'s ft_combat_after_action_resolved() (start_encounter_combat.php, deleted with its call in
//       janus.php): a committed action of a MEMBER of the open fight is a sound event (encounter-map/ft3d/combat-noise.ts: the used ability's loudness
//       class → a radius; a Silenced caster's verbal spell is silent) and pulls in whoever HEARS it (walls block, a closed door halves, deafened hears
//       nothing) — roll-init/client-combat-membership.ts's noise channel. Heard on a GM page from: `ft:hostile-action-committed` (the acting page —
//       A6's event; the GM's own creatures), and the committed peer frames of the ability lane (`ft:ability-truth-frame`) and the attack lane
//       (`ft:attack-truth-applied` carrying a committed wire frame) for a player's action. A legacy-routed (handed-back) action makes no noise any
//       more — worker pick (owner to review), R-131: the next round wrap's sweep still pulls in whoever sees / hears the fight.
// After SETTLE_MS of quiet (never more than MAX_WAIT_MS after the window's first trigger) the page plans a SWEEP join (websocket/combat-start-truth.ts planBrowserJoin: the island grown from the fight's members
// through the start's own resolver — sight, the baseline hearing of an opposing member, a member's summon, a lair's domain, + every noise source
// heard in the window — then janus-engine/combat/join-plan.ts: band slots + resets) and posts it QUIETLY through the combat-truth lane
// (`tryCommitSweepJoin`, public log rows — "notices the fight", or "hears the fighting" when a noise set it off); nobody joining = nothing posted.
// A trigger is bound to the FIGHT it was seen in: a fight that ended / was replaced before the sweep ran drops it (Codex N5d r3 #3). A plan that
// could not be made YET (the FT3D scene truth not ready, a compile / definition still loading) keeps the trigger pending and retries with a backoff
// that settles at one try a minute while that fight lasts (lane N5e — coordinator Codex round 4 #5: it used to be dropped after nine tries).
// GM pages only (the join commit's gate — worker pick N5d), top window only (the FT3D scene iframe loads this bundle too); the operation id is the
// plan's content, so several GM tabs land on one receipt.
import { tryCommitSweepJoin } from './combat-truth.js';
import { definitionOf } from './charge-truth.js';
import { fetchAndCacheObject } from './object-cache.js';
import { creatureIsInActiveCombat, type ClientCombatNoise } from '../roll-init/client-combat-membership.js';
import { ftCombatNoiseClassForAbility, ftCombatNoiseRadiusFt } from '../encounter-map/ft3d/combat-noise.js';
import { hasStatus } from '../encounter-map/ft3d/perception.js';
import { withAsyncDeadline } from '../shared/async-deadline.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const SETTLE_MS = 1500;
/** A stream of triggers (a busy fight) never postpones a sweep past this (Codex N5e TS-B #3 — every trigger used to re-arm the 1.5 s settle). */
const MAX_WAIT_MS = 6000;
/** The planner waits up to 20 s for scene truth. Allow another 5 s, then retry
 * from warm caches; an automatic sweep must not inherit the 180 s click watchdog. */
const SWEEP_TIMEOUT_MS = 25000;
/** Gates that mean "not yet" (retry), not "nothing to do". `island-moving`: the plan's island kept moving under it (Codex N5e TS-B #2). */
const TRANSIENT = new Set(['island-truth', 'compile-fetch-failed', 'join-definitions', 'encounter-arr', 'busy', 'island-moving', 'sweep-timeout']);
/** The retry backoff; past its end every retry waits the last step (the trigger is dropped only when its fight is over). */
const RETRY_MS = [3000, 6000, 12000, 24000, 48000, 60000];
/** StatusEffect `silenced` (includes/catalogs/StatusEffect.php) — ft_combat_noise_class()'s has_status_effect(caster, silenced). */
const SILENCED = 'BCC91DAF-AF10-497E-241D-ACD1BEC9E80E';
/** DamageType ids of the "explosion" proxy (includes/additions.php DamageType): thunder / fire / force / lightning. */
const BOOM_DAMAGE: Record<string, string> = {
  'F79CF36E-F439-44C3-9668-050683A30F1C': 'thunder', 'A8469481-849E-4BBA-986E-7C418DCA06A1': 'fire',
  '25F9052F-B963-43CB-991E-EB315E08A549': 'force', '944EC0D7-3320-42B1-B28D-68980DAA9719': 'lightning',
};
const emit = (name: string, detail: unknown): void => { window.dispatchEvent(new CustomEvent(name, { detail })); };

interface Pending { combatId: string; why: Set<string>; attempt: number; noise: Map<string, number>; since: number }
const rounds = new Map<string, { combatId: string; round: number }>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const pending = new Map<string, Pending>();
const inFlight = new Map<string, { combatId: string; startedAt: number; stage: string }>();

function encounterFor(id: string): Row | null { return asRow(window.FateTide?.encounterCache?.get?.(id) ?? window.FateTide?.encounterCache?.get?.(upper(id))); }
const rosterOf = (e: Row | null): Row[] => (Array.isArray(e?.encounter_creatures_arr) ? e!.encounter_creatures_arr as unknown[] : []).map(asRow).filter((r): r is Row => !!r);
const rowId = (r: Row): string => upper(asRow(r.encounter_creatures)?.encounter_creature_id);
/** The held encounter's open fight: its combat id, round and campaign (null when none is open). */
function heldFight(encounterId: string): { combatId: string; round: number; campaignId: string } | null {
  const e = encounterFor(encounterId);
  if (!e) return null;
  const b = asRow(e.encounters) ?? {};
  const pick = (k: string): unknown => scalar(k in b ? b[k] : e[k]);
  if (Number(pick('in_combat') ?? 0) !== 1) return null;
  const combatId = upper(pick('active_combat_id'));
  if (!UUID.test(combatId)) return null;
  return { combatId, round: Number(pick('current_round') ?? 0) || 0, campaignId: upper(pick('encounter_master_parent_id')) };
}
const mine = (): boolean => window.top === window && window.FateTide?.user?.isGM === true;

function arm(id: string, ms: number): void {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.set(id, setTimeout(() => { timers.delete(id); void run(id).catch(error => console.warn('[combat-sweep] sweep failed', error)); }, ms));
}
/** `expectCombatId`: the fight the trigger was seen in — a sound heard in fight A is never applied to a fight B that replaced it meanwhile (Codex N5e
 *  TS-B #1). */
function schedule(encounterId: string, why: string, noise?: ClientCombatNoise, expectCombatId?: string): void {
  const id = upper(encounterId);
  const fight = id && mine() ? heldFight(id) : null;
  if (!fight || (expectCombatId !== undefined && fight.combatId !== upper(expectCombatId))) return;
  let p = pending.get(id);
  // a trigger belongs to the fight it was seen in — a different fight replaces it (Codex N5d r3 #3)
  if (!p || p.combatId !== fight.combatId) { p = { combatId: fight.combatId, why: new Set(), attempt: 0, noise: new Map(), since: Date.now() }; pending.set(id, p); }
  p.why.add(why); p.attempt = 0;
  // several sounds in one window: every source, at its loudest
  if (noise) { const src = upper(noise.sourceId); p.noise.set(src, Math.max(p.noise.get(src) ?? 0, noise.radiusFt)); }
  // settle 1.5 s after the last trigger, but never later than MAX_WAIT_MS after the first one of this window
  if (!inFlight.has(id)) arm(id, Math.max(0, Math.min(SETTLE_MS, p.since + MAX_WAIT_MS - Date.now())));
}

function retry(encounterId: string, p: Pending): void {
  const newer = pending.get(encounterId);
  if (newer && newer.combatId === p.combatId) {
    for (const w of p.why) newer.why.add(w);
    for (const [s, r] of p.noise) newer.noise.set(s, Math.max(newer.noise.get(s) ?? 0, r));
  } else if (!newer) {
    pending.set(encounterId, { ...p, attempt: p.attempt + 1, since: Date.now() });
    arm(encounterId, RETRY_MS[Math.min(p.attempt, RETRY_MS.length - 1)]!);
  }
}

async function run(encounterId: string): Promise<void> {
  const p = pending.get(encounterId);
  if (!p) return;
  pending.delete(encounterId);
  if (!mine()) return;
  // A cache replacement can briefly leave no encounter. Absence is not evidence
  // that this fight ended; its identity is checked again when the read returns.
  if (!encounterFor(encounterId)) { retry(encounterId, p); return; }
  const fight = heldFight(encounterId);
  if (!fight || fight.combatId !== p.combatId || !UUID.test(fight.campaignId)) return; // that fight is over
  const flight = { combatId: fight.combatId, startedAt: Date.now(), stage: 'starting' };
  inFlight.set(encounterId, flight);
  let gate: string | null = null;
  const note = (e: Event): void => { const d = (e as CustomEvent).detail; if (d?.action === 'join') gate = String(d.reason ?? ''); };
  window.addEventListener('ft:combat-slice-unavailable', note);
  try {
    const noise = [...p.noise].map(([sourceId, radiusFt]) => ({ sourceId, radiusFt }));
    // A REJECTED commit (a throw in the planner / transport — never a decided outcome) is a "not yet" too (lane N5f — coordinator Codex round 8: it
    // used to drop the trigger, and its sound, for good).
    let outcome: Awaited<ReturnType<typeof tryCommitSweepJoin>> = null;
    let threw = false;
    try { outcome = await withAsyncDeadline(signal => tryCommitSweepJoin({ campaignId: fight.campaignId, encounterId, combatId: fight.combatId, noise,
      signal, progress: stage => { signal.throwIfAborted(); flight.stage = stage; } }), SWEEP_TIMEOUT_MS); }
    catch (error) {
      threw = true;
      gate = error instanceof DOMException && error.name === 'TimeoutError' ? 'sweep-timeout' : 'rejected';
      console.warn('[combat-sweep] sweep commit rejected', error);
    }
    emit('ft:combat-sweep-posted', { encounterId, combatId: fight.combatId, why: [...p.why], noise, outcome, gate });
    // "not yet" keeps the trigger (bounded backoff, then once a minute while the fight lasts) unless a newer one already took its place
    if (threw || (outcome !== 'handled' && gate && TRANSIENT.has(gate))) {
      retry(encounterId, p);
    }
  } finally {
    window.removeEventListener('ft:combat-slice-unavailable', note);
    inFlight.delete(encounterId);
    // a trigger that arrived while this one ran — settled within the same 6 s cap as any window (Codex N5e r2 E)
    const next = pending.get(encounterId);
    if (next && !timers.has(encounterId)) arm(encounterId, Math.max(0, Math.min(SETTLE_MS, next.since + MAX_WAIT_MS - Date.now())));
  }
}

/** (b) the round wrap: the held fight's round went up (a new fight is the start's island, never a sweep). */
function watchRound(encounterId: string): void {
  const id = upper(encounterId);
  if (!id) return;
  const fight = heldFight(id);
  if (!fight) { rounds.delete(id); return; }
  const prev = rounds.get(id);
  rounds.set(id, { combatId: fight.combatId, round: fight.round });
  if (prev && prev.combatId === fight.combatId && fight.round > prev.round) schedule(id, 'round-wrap');
}

// ---- (c) the hostile action's noise ----
const heard = new Set<string>();
const HEARD_MAX = 2048;
/** An action heard before this page's roster carries the fight / the caster's membership (the action's event can outrun the frame that put the
 *  caster in the fight) is retried, NOT marked heard (coordinator Codex round 6 — it used to be marked first and lost for good). */
const notYet = new Map<string, number>();
const NOT_YET_TRIES = 6, NOT_YET_MS = 1000;
/** The fight this page held when it FIRST heard each operation (null = none: the action happened outside a fight) — every later copy of that operation
 *  (a retry, or a duplicate committed event) is judged against it, never against whichever fight is open by then (lane N5f — coordinator Codex round 8
 *  + Codex N5f B #1: a sound from fight A could pull listeners into a fight B that replaced it). Bounded like `heard`. */
const firstHeard = new Map<string, string | null>();
type HeardAction = { encounterId?: unknown; casterId?: unknown; abilityId?: unknown; operationId?: unknown };
const settle = (op: string): void => { notYet.delete(op); heard.add(op); if (heard.size > HEARD_MAX) heard.delete(heard.values().next().value!); };
/** ft_combat_after_action_resolved(): a committed action of a MEMBER of the open fight is a sound event of its ability's loudness. Once per operation. */
async function hearAction(d: HeardAction): Promise<void> {
  const encounterId = upper(d.encounterId), casterId = upper(d.casterId), abilityId = upper(d.abilityId), op = upper(d.operationId);
  if (!mine() || !UUID.test(encounterId) || !UUID.test(casterId) || !op || heard.has(op)) return;
  const encounter = encounterFor(encounterId);
  const caster = rosterOf(encounter).find(r => rowId(r) === casterId);
  const fight = heldFight(encounterId);
  if (!firstHeard.has(op)) { firstHeard.set(op, fight?.combatId ?? null); if (firstHeard.size > HEARD_MAX) firstHeard.delete(firstHeard.keys().next().value!); }
  const bound = firstHeard.get(op) ?? null;
  // No fight held when the action was first heard: no sound event (PHP's gate — an action outside a fight pulls nobody in). Its fight no longer the
  // open one: dropped (that fight is over). Either way the operation is settled — a later copy never re-binds it.
  if (bound === null || !fight || fight.combatId !== bound) { settle(op); return; }
  if (!caster || !creatureIsInActiveCombat(encounter, caster)) {
    // the caster is not (yet) a member of the fight as this page holds it — the roster may not have caught up (the action's event can outrun the
    // join frame): look again a few times (bound to the first-heard fight above)
    const tries = (notYet.get(op) ?? 0) + 1;
    if (tries <= NOT_YET_TRIES) { notYet.set(op, tries); setTimeout(() => hear(d), NOT_YET_MS); } else notYet.delete(op);
    return;
  }
  settle(op); // processed from here on (marked before the definition read, so a second copy of the same action never doubles it)
  // The used ability's definition (the page's object cache, else one read); an unreadable one is an ordinary 'normal' action.
  let ability = UUID.test(abilityId) ? definitionOf(abilityId) : null;
  if (!ability && UUID.test(abilityId)) { await fetchAndCacheObject(abilityId).catch(() => null); ability = definitionOf(abilityId); }
  const loudness = ftCombatNoiseClassForAbility(ability, { casterSilenced: hasStatus(caster, SILENCED), damageTypeVariableName: id => BOOM_DAMAGE[upper(id)] });
  const radiusFt = ftCombatNoiseRadiusFt(loudness);
  emit('ft:combat-noise-heard', { encounterId, casterId, abilityId, operationId: op, loudness, radiusFt });
  if (radiusFt <= 0) return; // silent: nothing carries (ft_combat_noise_event → null)
  schedule(encounterId, 'noise', { sourceId: casterId, radiusFt }, fight.combatId); // the fight the action was heard in, not whichever is open after the read
}
const hear = (d: unknown): void => { void hearAction(asRow(d) ?? {}).catch(error => console.warn('[combat-sweep] noise failed', error)); };
// the acting page (A6, 2026-10-02 — a GM-controlled creature's ability / attack): {encounterId, casterId, abilityId, operationId}
window.addEventListener('ft:hostile-action-committed', (event: Event) => hear((event as CustomEvent).detail));
// a player's ability use, as a peer GM page receives it (the committed frame — a correction or provisional frame is no committed action)
window.addEventListener('ft:ability-truth-frame', (event: Event) => {
  const f = asRow((event as CustomEvent).detail);
  if (!f || f.committed !== true || f.correction === true || f.zone_entry || f.fall || f.move_trigger) return;
  const verdict = asRow(f.verdict);
  if (Array.isArray(verdict?.rows) && verdict.rows.some(log => asRow(log)?.roll_type_1 === 'janus_unported_skip'
      && asRow(log)?.roll_type_2 === 'death_refusal')) return;
  hear({ encounterId: f.encounter_id, casterId: f.actor_id, abilityId: f.ability_id, operationId: f.operation_id });
});
// a player's weapon attack, as a peer GM page receives it (render() hands the committed wire frame through)
window.addEventListener('ft:attack-truth-applied', (event: Event) => {
  const c = asRow(asRow((event as CustomEvent).detail)?.command);
  if (!c || c.committed !== true || c.correction === true || c.swing_skipped === true) return;
  hear({ encounterId: c.encounter_id, casterId: c.actor_id, abilityId: c.weapon_id, operationId: c.operation_id });
});

// (a) a reveal: the compact privacy frame names the toggled row; a public one in an encounter holding an open fight → sweep.
window.addEventListener('ft:truth-frame-applied', (event: Event) => {
  const frame = asRow((event as CustomEvent).detail);
  if (!String(frame?.operation_id ?? '').startsWith('card-edit:') || !Array.isArray(frame?.rows)) return;
  if (frame.rows.some(value => {
    const write = asRow(value), row = asRow(write?.row);
    return write?.table === 'encounter_creatures' && row && Object.prototype.hasOwnProperty.call(row, 'encounter_creature_private')
      && Number(scalar(row.encounter_creature_private)) === 0;
  })) schedule(upper(frame.encounter_id), 'reveal');
});
window.addEventListener('ft:encounter-state-received', (event: Event) => {
  try {
    const d = (event as CustomEvent).detail ?? {};
    const id = upper(d.encounterId);
    if (d.subType === 'encounter_creature_private_toggle') {
      const rows = Array.isArray(asRow(d.encounterArr)?.encounter_creatures_arr) ? (asRow(d.encounterArr)!.encounter_creatures_arr as unknown[]) : [];
      if (rows.some(r => Number(scalar(asRow(asRow(r)?.encounter_creatures)?.encounter_creature_private) ?? 1) === 0)) schedule(id, 'reveal');
      return;
    }
    watchRound(id);
  } catch (error) { console.warn('[combat-sweep] reveal watch failed', error); }
});
window.addEventListener('ft:encounter-turn-state', (event: Event) => {
  try { watchRound(upper((event as CustomEvent).detail?.encounterId)); } catch (error) { console.warn('[combat-sweep] round watch failed', error); }
});
Object.assign(window, { ftCombatSweepSchedule: schedule, ftCombatHearAction: hear,
  ftCombatSweepState: () => ({ at: Date.now(), timeoutMs: SWEEP_TIMEOUT_MS,
    inFlight: [...inFlight].map(([encounterId, flight]) => ({ encounterId, ...flight })),
    pending: [...pending].map(([encounterId, p]) => ({ encounterId, combatId: p.combatId, since: p.since, attempt: p.attempt,
      why: [...p.why], noise: [...p.noise], armed: timers.has(encounterId) })) }),
});
